// src/pages/admin/AdminManagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  Avatar,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
  Alert,
  Snackbar,
  useTheme,
  alpha,
  Tooltip,
  Switch,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  SupervisorAccount as SuperAdminIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Shield as ShieldIcon,
  Close as CloseIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import * as adminApi from '../../services/admin';

interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  isSuperAdmin: boolean;
  isActive: boolean;
  createdAt: string;
  permissions: string[];
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const AVAILABLE_PERMISSIONS = [
  { value: 'user_management', label: 'User Management', description: 'Manage members, experts, approve/reject users' },
  { value: 'dashboard', label: 'Dashboard', description: 'View dashboard statistics' },
  { value: 'industries', label: 'Industries', description: 'Manage industries and sub-industries' },
  { value: 'marketplace', label: 'Marketplace', description: 'Manage marketplace projects and investor requests' },
  { value: 'content_management', label: 'Content Management', description: 'Manage articles, innovations, testimonials' },
  { value: 'admin_management', label: 'Admin Management', description: 'Create and manage other admins (super admin only)' },
  { value: 'home_content', label: 'Home Content', description: 'Edit home page content' },
  { value: 'support_messages', label: 'Support Messages', description: 'View and respond to support messages' }
];

const AdminManagement: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<AdminUser | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Create admin form state
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    isSuperAdmin: false,
    permissions: [] as string[]
  });

  // Edit permissions form state
  const [editPermissions, setEditPermissions] = useState<string[]>([]);

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const data = await adminApi.listAdmins();
      setAdmins(data);
    } catch (error: any) {
      console.error('Error fetching admins:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to load admins',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async () => {
    // Validation
    if (!createForm.email || !createForm.password || !createForm.firstName || !createForm.lastName) {
      setSnackbar({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error'
      });
      return;
    }

    if (createForm.password.length < 8) {
      setSnackbar({
        open: true,
        message: 'Password must be at least 8 characters',
        severity: 'error'
      });
      return;
    }

    if (!createForm.isSuperAdmin && createForm.permissions.length === 0) {
      setSnackbar({
        open: true,
        message: 'Please select at least one permission for sub-admin',
        severity: 'error'
      });
      return;
    }

    try {
      setSubmitting(true);
      await adminApi.createAdmin(createForm);

      setSnackbar({
        open: true,
        message: 'Admin created successfully',
        severity: 'success'
      });

      // Reset form and close dialog
      setCreateForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        isSuperAdmin: false,
        permissions: []
      });
      setCreateDialogOpen(false);

      // Refresh admin list
      fetchAdmins();
    } catch (error: any) {
      console.error('Error creating admin:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to create admin',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditPermissions = (admin: AdminUser) => {
    setSelectedAdmin(admin);
    setEditPermissions(admin.permissions);
    setEditDialogOpen(true);
  };

  const handleSavePermissions = async () => {
    if (!selectedAdmin) return;

    try {
      setSubmitting(true);
      await adminApi.updateAdminPermissions(selectedAdmin.id, { permissions: editPermissions });

      setSnackbar({
        open: true,
        message: 'Permissions updated successfully',
        severity: 'success'
      });

      setEditDialogOpen(false);
      fetchAdmins();
    } catch (error: any) {
      console.error('Error updating permissions:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to update permissions',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin: AdminUser) => {
    try {
      await adminApi.toggleAdminStatus(admin.id, { isActive: !admin.isActive });

      setSnackbar({
        open: true,
        message: `Admin ${!admin.isActive ? 'activated' : 'deactivated'} successfully`,
        severity: 'success'
      });

      fetchAdmins();
    } catch (error: any) {
      console.error('Error toggling admin status:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to update admin status',
        severity: 'error'
      });
    }
  };

  const handleDeleteAdmin = async () => {
    if (!selectedAdmin) return;

    try {
      setSubmitting(true);
      await adminApi.deleteAdmin(selectedAdmin.id);

      setSnackbar({
        open: true,
        message: 'Admin deleted successfully',
        severity: 'success'
      });

      setDeleteDialogOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (error: any) {
      console.error('Error deleting admin:', error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.error?.message || 'Failed to delete admin',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePermissionToggle = (permission: string) => {
    if (createDialogOpen) {
      setCreateForm(prev => ({
        ...prev,
        permissions: prev.permissions.includes(permission)
          ? prev.permissions.filter(p => p !== permission)
          : [...prev.permissions, permission]
      }));
    } else if (editDialogOpen) {
      setEditPermissions(prev =>
        prev.includes(permission)
          ? prev.filter(p => p !== permission)
          : [...prev, permission]
      );
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Admin Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create and manage admin users with granular permissions
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
          sx={{ borderRadius: 2, px: 3 }}
        >
          Create New Admin
        </Button>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 56, height: 56 }}>
                  <AdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>{admins.length}</Typography>
                  <Typography variant="body2" color="text.secondary">Total Admins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 56, height: 56 }}>
                  <SuperAdminIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {admins.filter(a => a.isSuperAdmin).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Super Admins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)` }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: theme.palette.success.main, width: 56, height: 56 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h4" fontWeight={700}>
                    {admins.filter(a => !a.isSuperAdmin).length}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Sub-Admins</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Admin List Table */}
      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
              <TableCell sx={{ fontWeight: 600 }}>Admin</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Permissions</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <PersonIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary">
                    No admins found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Only super admins can view the admin list
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => (
                <TableRow key={admin.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: admin.isSuperAdmin ? theme.palette.warning.main : theme.palette.primary.main }}>
                        {admin.firstName[0]}{admin.lastName[0]}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {admin.firstName} {admin.lastName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {admin.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={admin.isSuperAdmin ? <SuperAdminIcon /> : <ShieldIcon />}
                      label={admin.isSuperAdmin ? 'Super Admin' : 'Sub-Admin'}
                      color={admin.isSuperAdmin ? 'warning' : 'primary'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={admin.isActive ? <LockOpenIcon /> : <LockIcon />}
                      label={admin.isActive ? 'Active' : 'Inactive'}
                      color={admin.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {admin.isSuperAdmin ? (
                      <Chip label="All Permissions" size="small" color="warning" variant="outlined" />
                    ) : (
                      <Tooltip title={admin.permissions.join(', ')}>
                        <Chip label={`${admin.permissions.length} permissions`} size="small" variant="outlined" />
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(admin.createdAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {!admin.isSuperAdmin && (
                        <Tooltip title="Edit Permissions">
                          <IconButton
                            size="small"
                            onClick={() => handleEditPermissions(admin)}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title={admin.isActive ? 'Deactivate' : 'Activate'}>
                        <Switch
                          checked={admin.isActive}
                          onChange={() => handleToggleStatus(admin)}
                          size="small"
                          disabled={admin.isSuperAdmin}
                        />
                      </Tooltip>
                      {!admin.isSuperAdmin && (
                        <Tooltip title="Delete Admin">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setSelectedAdmin(admin);
                              setDeleteDialogOpen(true);
                            }}
                            sx={{ color: theme.palette.error.main }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Admin Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !submitting && setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h5" fontWeight={600}>Create New Admin</Typography>
            <IconButton onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="First Name"
                value={createForm.firstName}
                onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
                disabled={submitting}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={createForm.lastName}
                onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
                disabled={submitting}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="email"
                label="Email Address"
                value={createForm.email}
                onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                disabled={submitting}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="password"
                label="Password"
                value={createForm.password}
                onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                disabled={submitting}
                helperText="Minimum 8 characters"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={createForm.isSuperAdmin}
                    onChange={(e) => setCreateForm({ ...createForm, isSuperAdmin: e.target.checked, permissions: [] })}
                    disabled={submitting}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={600}>Super Admin</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Has full access to all features and can manage other admins
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {!createForm.isSuperAdmin && (
              <Grid item xs={12}>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  Select Permissions
                </Typography>
                <FormGroup>
                  {AVAILABLE_PERMISSIONS.map((perm) => (
                    <FormControlLabel
                      key={perm.value}
                      control={
                        <Checkbox
                          checked={createForm.permissions.includes(perm.value)}
                          onChange={() => handlePermissionToggle(perm.value)}
                          disabled={submitting}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{perm.label}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {perm.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </FormGroup>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateAdmin}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={submitting}
          >
            {submitting ? 'Creating...' : 'Create Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Permissions Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !submitting && setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>Edit Permissions</Typography>
        </DialogTitle>
        <DialogContent dividers>
          {selectedAdmin && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Editing permissions for: <strong>{selectedAdmin.firstName} {selectedAdmin.lastName}</strong>
              </Typography>
              <FormGroup sx={{ mt: 2 }}>
                {AVAILABLE_PERMISSIONS.map((perm) => (
                  <FormControlLabel
                    key={perm.value}
                    control={
                      <Checkbox
                        checked={editPermissions.includes(perm.value)}
                        onChange={() => handlePermissionToggle(perm.value)}
                        disabled={submitting}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{perm.label}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {perm.description}
                        </Typography>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setEditDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePermissions}
            variant="contained"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
            disabled={submitting}
          >
            {submitting ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !submitting && setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this admin account?
          </Typography>
          {selectedAdmin && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <strong>{selectedAdmin.firstName} {selectedAdmin.lastName}</strong> will be permanently removed and will lose access to the admin panel.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAdmin}
            variant="contained"
            color="error"
            startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
            disabled={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete Admin'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminManagement;
